import { useState, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Factory,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Package,
  Droplets,
  Wind,
  Shirt,
  Box,
  ThumbsUp,
  Eye,
  Search,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/page-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useOrders, useUpdateOrderStatus } from '@/hooks/use-orders';
import { useStartStep, useCompleteStep } from '@/hooks/use-order-steps';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, cn } from '@/lib/utils';
import type { ApiOrder } from '@/types';

import { KanbanBoard } from '@/components/production/KanbanBoard';

export const Route = createFileRoute('/production')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <ProductionBoard />
      </MainLayout>
    </ProtectedRoute>
  ),
});

const STEPS = [
  { key: 'RECEIVED', label: 'Received', icon: Package, color: 'bg-blue-500' },
  { key: 'WASHING', label: 'Washing', icon: Droplets, color: 'bg-cyan-500' },
  { key: 'DRYING', label: 'Drying', icon: Wind, color: 'bg-amber-500' },
  { key: 'IRONING', label: 'Ironing', icon: Shirt, color: 'bg-orange-500' },
  { key: 'PACKING', label: 'Packing', icon: Box, color: 'bg-purple-500' },
  { key: 'READY', label: 'Ready', icon: ThumbsUp, color: 'bg-green-500' },
] as const;

import { InvoiceDetailDialog } from '@/components/orders/invoice-detail-dialog';

function ProductionBoard() {
  const [activeTab, setActiveTab] = useState('RECEIVED');
  const [viewMode, setViewMode] = useState<'board' | 'list'>('list');
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const { toast } = useToast();
  const navigate = useNavigate();

  // Fetch all active orders
  const { data: ordersRes, isLoading } = useOrders({
    status: undefined,
    page: 1,
  });

  const startStep = useStartStep();
  const completeStep = useCompleteStep();
  const updateStatus = useUpdateOrderStatus();

  const orders = useMemo(() => ordersRes?.data || [], [ordersRes?.data]);

  const filteredOrders = useMemo(() => {
    if (!searchQuery.trim()) return orders;
    const q = searchQuery.toLowerCase();
    return orders.filter(o => 
      o.orderNumber.toLowerCase().includes(q) || 
      o.customer?.name?.toLowerCase().includes(q) ||
      o.customer?.phone?.toLowerCase().includes(q)
    );
  }, [orders, searchQuery]);

  const handleAdvance = async (order: ApiOrder) => {
    const currentStatus = order.orderStatus?.toUpperCase() || 'RECEIVED';
    const currentIdx = STEPS.findIndex((s) => s.key === currentStatus);
    if (currentIdx < 0 || currentIdx >= STEPS.length - 1) return;

    const nextStep = STEPS[currentIdx + 1].key;
    
    try {
      await completeStep.mutateAsync({ invoiceId: order.id, step: currentStatus });
      await startStep.mutateAsync({ invoiceId: order.id, step: nextStep });
      toast({
        title: 'Status Updated',
        description: `Order ${order.orderNumber} advanced to ${nextStep}`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to advance step',
        variant: 'destructive',
      });
    }
  };

  const handleStatusJump = async (order: ApiOrder, newStatus: string) => {
    try {
      await updateStatus.mutateAsync({ orderId: order.id, status: newStatus });
      toast({
        title: 'Status Jumped',
        description: `Order ${order.orderNumber} is now ${newStatus}`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const handleViewDetails = (id: string) => {
    navigate({ to: '/orders/$id', params: { id } });
  };

  const totalActive = useMemo(() => orders.filter(o => 
    !['DELIVERED', 'CANCELLED'].includes(o.orderStatus?.toUpperCase() || '')
  ).length, [orders]);

  // Group orders by current status
  const ordersByStatus = useMemo(() => {
    return STEPS.reduce(
      (acc, step) => {
        acc[step.key] = filteredOrders.filter((o) => {
          const orderStatus = o.orderStatus?.toUpperCase() || "RECEIVED";
          if (orderStatus === step.key) return true;
          if (orderStatus === "DRAFT" && step.key === "RECEIVED") return true;
          if (orderStatus === "PROCESSING" && step.key === "WASHING") return true;
          return false;
        });
        return acc;
      },
      {} as Record<string, ApiOrder[]>,
    );
  }, [filteredOrders]);

  const activeOrdersInTab = useMemo(() => {
    return ordersByStatus[activeTab] || [];
  }, [ordersByStatus, activeTab]);

  return (
    <div className="flex flex-col gap-6 min-w-0 max-w-full h-full animate-in fade-in duration-700">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <PageHeader
          title="Production Workflow"
          description={`${totalActive} orders currently in progress`}
        />
        <div className="flex bg-zinc-100 dark:bg-zinc-900 p-1 rounded-xl w-fit">
          <Button 
            variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
            size="sm" 
            onClick={() => setViewMode('list')}
            className="rounded-lg font-bold text-[10px] uppercase tracking-widest px-4 h-9"
          >
            List
          </Button>
          <Button 
            variant={viewMode === 'board' ? 'secondary' : 'ghost'} 
            size="sm" 
            onClick={() => setViewMode('board')}
            className="rounded-lg font-bold text-[10px] uppercase tracking-widest px-4 h-9"
          >
            Kanban
          </Button>
        </div>
      </div>

      {/* Global Search Bar */}
      <div className="relative group max-w-2xl mx-auto w-full px-4 mb-2">
        <div className="absolute inset-y-0 left-8 flex items-center pointer-events-none text-muted-foreground group-focus-within:text-blue-500 transition-colors">
          <Search className="h-4 w-4" />
        </div>
        <Input
          placeholder="Search by Order # or Customer..."
          className="h-14 pl-12 pr-6 rounded-2xl border-2 bg-white dark:bg-zinc-950 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all text-sm font-medium shadow-sm group-hover:shadow-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {viewMode === 'board' ? (
        <div className="flex-1 min-h-0">
          <KanbanBoard
            orders={filteredOrders}
            isLoading={isLoading}
            onAdvance={handleAdvance}
            onViewDetails={handleViewDetails}
          />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 flex-1 flex flex-col min-h-0">
          <div className="overflow-x-auto pb-2 scrollbar-none">
            <TabsList className="bg-transparent h-auto p-0 gap-2 flex flex-nowrap w-max sm:w-full">
              {STEPS.map((step) => {
                const count = ordersByStatus[step.key]?.length || 0;
                const StepIcon = step.icon;
                return (
                  <TabsTrigger 
                    key={step.key} 
                    value={step.key}
                    className="flex-1 min-w-[120px] rounded-2xl border-2 py-4 h-auto data-[state=active]:border-blue-600 data-[state=active]:bg-blue-50/50 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900/10 dark:data-[state=active]:text-blue-400 dark:data-[state=active]:border-blue-500/50 transition-all duration-300"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className={cn(
                        "h-8 w-8 rounded-xl flex items-center justify-center transition-transform duration-300",
                        activeTab === step.key ? "scale-110" : "opacity-40"
                      )}>
                        <StepIcon className="h-5 w-5" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black uppercase tracking-widest">{step.label}</span>
                        <span className="text-[10px] font-bold opacity-50">{count} Orders</span>
                      </div>
                    </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin rounded-3xl border-2 border-zinc-100 dark:border-zinc-900 bg-white dark:bg-zinc-950 shadow-xl min-h-[400px]">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-32 opacity-20">
                <Loader2 className="h-10 w-10 animate-spin mb-4" />
                <p className="font-black uppercase tracking-widest text-[10px]">Syncing Workflow State...</p>
              </div>
            ) : (
              <div className="p-2 sm:p-4 space-y-4">
                {activeOrdersInTab.length > 0 ? (
                  activeOrdersInTab.map((order) => {
                    const step = STEPS.find(s => s.key === activeTab)!;
                    const StepIcon = step.icon;
                    return (
                      <div
                        key={order.id}
                        onClick={() => handleViewDetails(order.id)}
                        className="group flex flex-col sm:flex-row sm:items-center justify-between p-5 rounded-2xl border-2 border-zinc-50 dark:border-zinc-900/50 bg-white dark:bg-zinc-950 hover:bg-zinc-50/50 dark:hover:bg-zinc-900/30 transition-all duration-300 hover:shadow-md hover:translate-y-[-2px] relative overflow-hidden cursor-pointer"
                      >
                        <div className="absolute top-0 left-0 bottom-0 w-1 bg-zinc-100 dark:bg-zinc-800 group-hover:bg-blue-600 transition-colors" />
                        
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={cn("hidden sm:flex h-12 w-12 rounded-xl items-center justify-center border-2 border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 shadow-inner group-hover:border-blue-100 dark:group-hover:border-blue-900/30")}>
                            <StepIcon className="h-5 w-5 text-muted-foreground group-hover:text-blue-600 transition-colors" />
                          </div>
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-black text-foreground group-hover:text-blue-600 transition-colors tracking-tight">#{order.orderNumber}</span>
                              <Badge className="bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 border-none font-black text-[9px] uppercase tracking-tighter">
                                {order.customer?.name || 'Walk-in'}
                              </Badge>
                            </div>
                            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-widest flex items-center gap-1.5 line-clamp-1">
                              <span className="font-bold text-zinc-400">Total:</span> 
                              {formatCurrency(order.totalAmount)}
                              <span className="opacity-20">•</span>
                              <span className="font-bold text-zinc-400">Items:</span>
                              {order.items?.length || 0} services
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mt-4 sm:mt-0 justify-end">
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="h-10 px-4 rounded-xl font-bold text-[10px] uppercase tracking-widest gap-2 bg-white dark:bg-zinc-900"
                            onClick={() => handleViewDetails(order.id)}
                          >
                            <Eye className="h-4 w-4" />
                            Details
                          </Button>
                          {activeTab !== 'READY' && (
                            <DropdownMenu>
                              <div className="flex items-center -space-x-px">
                                <Button
                                  size="sm"
                                  className="h-10 px-6 rounded-l-xl rounded-r-none font-black text-[10px] uppercase tracking-widest gap-2 bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-zinc-200 border-r border-white/10 dark:border-zinc-800/50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAdvance(order);
                                  }}
                                  disabled={startStep.isPending || completeStep.isPending || updateStatus.isPending}
                                >
                                  Advance
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="sm"
                                    className="h-10 px-2 rounded-r-xl rounded-l-none bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-zinc-200"
                                    disabled={startStep.isPending || completeStep.isPending || updateStatus.isPending}
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                              </div>
                              <DropdownMenuContent align="end" className="w-48 rounded-2xl p-2 border-2 shadow-2xl backdrop-blur-xl bg-white/95 dark:bg-zinc-950/95">
                                <div className="px-2 py-1.5 mb-1">
                                  <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground opacity-50">Jump to Status</p>
                                </div>
                                {STEPS.map((s) => {
                                  // Only show future steps in the jump menu to avoid confusion
                                  const currentIdx = STEPS.findIndex(step => step.key === order.orderStatus?.toUpperCase());
                                  const targetIdx = STEPS.findIndex(step => step.key === s.key);
                                  
                                  if (targetIdx <= currentIdx) return null;

                                  return (
                                    <DropdownMenuItem
                                      key={s.key}
                                      className="rounded-xl font-bold text-[10px] uppercase tracking-widest gap-3 py-3 px-3 cursor-pointer focus:bg-blue-50 focus:text-blue-600 dark:focus:bg-blue-900/20 dark:focus:text-blue-400"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStatusJump(order, s.key);
                                      }}
                                    >
                                      <div className={cn("h-2 w-2 rounded-full", s.color)} />
                                      {s.label}
                                    </DropdownMenuItem>
                                  );
                                })}
                                <DropdownMenuSeparator className="my-2 bg-zinc-100 dark:bg-zinc-800" />
                                <DropdownMenuItem 
                                  className="rounded-xl font-bold text-[10px] uppercase tracking-widest py-3 px-3 cursor-pointer text-red-500 focus:bg-red-50 focus:text-red-600 dark:focus:bg-red-900/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStatusJump(order, 'CANCELLED');
                                  }}
                                >
                                  Cancel Order
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-32 text-muted-foreground animate-in zoom-in duration-500">
                    <div className="h-20 w-20 bg-zinc-50 dark:bg-zinc-900 rounded-full flex items-center justify-center mb-6 border-2 border-dashed">
                      <Package className="h-10 w-10 opacity-10" />
                    </div>
                    <h3 className="text-xl font-black text-foreground mb-1">Queue Empty</h3>
                    <p className="text-xs font-bold uppercase tracking-widest opacity-40">No orders currently in {activeTab}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </Tabs>
      )}

      <InvoiceDetailDialog 
        open={isDetailOpen} 
        onOpenChange={setIsDetailOpen} 
        orderId={detailOrderId} 
      />
    </div>
  );
}
